import { type JSX, useEffect, useRef, useState } from "react"
import { t } from "@lingui/core/macro"
import { isDesktop } from "react-device-detect"
import { useVisibility } from "../../../hooks/useVisibility"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import {
    messagingApi,
    useLazyGetThreadsQuery,
} from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import { Button, ButtonColor, ButtonSize, Typography } from "../../common"
import { Envelope } from "../../common/atoms/Icons/Chat"
import { Spinner } from "../../common/atoms/Spinner"
import { ScrollButton } from "../../common/molecules/ScrollButton"
import { ScrollButtonDirection } from "../../common/molecules/ScrollButton/ScrollButton"
import { CAUTION_PASSWORD } from "../constants"
import { draftCache, useCacheSubscription } from "../DraftCache/DraftCache"
import { useMessagingView } from "../hooks"
import { useOrientation } from "../hooks/useOrientation"
import { MessageThread } from "../MessageThread/MessageThread"
import { classNames } from "../utils"
import styles from "./ThreadList.module.scss"
import { useInfiniteScroll } from "./useInfiniteScroll"
import { draftMessageToThread } from "./utils"
import { GeneralViewModel } from "./viewModels/GeneralViewModel"
import { UnifiedViewModel } from "./viewModels/UnifiedViewModel"
import type { Thread } from "../types"

type Props = {
    /** Whether the view is mobile or not. */
    isMobile: boolean
    /** Search prefix text entered in the search box. Use this to pass to the search request. */
    filteredName: string
    /** Whether or not the search text box is in focus. */
    searchFocused: boolean
    /** Notify the parent regarding has_conversation_features which comes from the search response. */
    permissionUpdate: (allow: boolean) => void
}

/**
 * Handles the fetching and displaying of threads including ghost conversations.
 * The request will filter results based on the filteredName. Drafts will not be
 * shown if the filteredName has text.
 * @param {Props} See the object for more details.
 * @returns {JSX.Element} The rendered component.
 */
export const ThreadList: React.FC<Props> = ({
    isMobile,
    filteredName,
    searchFocused,
    permissionUpdate,
}: Props): JSX.Element => {
    const dispatch = useAppDispatch()
    const { ref: visibilityRef, isVisible } = useVisibility()
    const messagingView = useMessagingView()
    const isLandscape = useOrientation()
    const ENVELOPE_ICON_LANDSCAPE_SIZE = 112
    const ENVELOPE_ICON_PORTRAIT_SIZE = 168
    const ENVELOPE_ICON_DESKTOP_SIZE = 120
    let ENVELOPE_ICON_SIZE
    if (isMobile) {
        ENVELOPE_ICON_SIZE = isLandscape
            ? ENVELOPE_ICON_LANDSCAPE_SIZE
            : ENVELOPE_ICON_PORTRAIT_SIZE
    } else {
        ENVELOPE_ICON_SIZE = ENVELOPE_ICON_DESKTOP_SIZE
    }
    const [showScrollUpButton, setShowScrollUpButton] = useState(false)
    const scrollRef = useRef<HTMLDivElement>(null)
    useInfiniteScroll(scrollRef, filteredName, searchFocused)
    useCacheSubscription()

    const vmProps = {
        dispatch,
    }
    const { handleUserClick } = messagingView.isDesktopUnified
        ? UnifiedViewModel(vmProps)
        : GeneralViewModel(vmProps)

    const lastQuery = useAppSelector(
        (state) =>
            messagingApi.endpoints.getThreads.select({
                offset: 0,
                search_username: filteredName,
            })(state).originalArgs,
    )

    // Get the current cached data
    const cachedData = useAppSelector(
        (state) =>
            messagingApi.endpoints.getThreads.select({
                offset: lastQuery?.offset ?? 0,
                search_username: filteredName,
            })(state).data,
    )

    const [triggerGetThreads, { data, isLoading, isFetching, error }] =
        useLazyGetThreadsQuery()

    const effectiveData = data || cachedData
    const hasNoMessages = effectiveData?.threads?.length === 0
    const hasConversationFeatures = effectiveData?.has_conversation_features

    const updateThreads = () => {
        triggerGetThreads({
            offset: lastQuery?.offset ?? 0,
            search_username: filteredName,
        })
    }

    useEffect(() => {
        if (isVisible && !cachedData && !data) {
            updateThreads()
        }
    }, [isVisible, lastQuery, triggerGetThreads, cachedData, data])

    useEffect(() => {
        if (isVisible) {
            triggerGetThreads({
                offset: 0,
                search_username: filteredName,
            })
        }
        if (searchFocused && scrollRef?.current?.scrollTo) {
            scrollRef.current.scrollTo({
                top: 0,
            })
            setShowScrollUpButton(false)
        }
    }, [filteredName])

    useEffect(() => {
        if (hasConversationFeatures !== undefined) {
            permissionUpdate(hasConversationFeatures)
        }
    }, [hasConversationFeatures])

    const getMessage = (): string | null => {
        const isTransitioningFromSearchToInbox =
            isFetching &&
            !filteredName &&
            data &&
            scrollRef?.current?.scrollTop === 0
        if (isLoading || isTransitioningFromSearchToInbox) return t`Loading...`
        if (error) return t`Error loading conversations`
        return null
    }

    const conversationUsers =
        effectiveData?.threads?.map((thread) => thread.other_user.username) ||
        []
    const draftThreads = [...draftCache.getAll()]
        .filter(
            ([username, draft]) =>
                !conversationUsers.includes(username) &&
                !draft.hasExistingThread,
        )
        .map(([username, draft]) => draftMessageToThread(username, draft))
        .sort((a, b) => b.created_at - a.created_at)

    const message = getMessage()
    if (message) {
        return (
            <div
                ref={visibilityRef as React.RefObject<HTMLDivElement>}
                className={classNames(styles.threadListMessage, {
                    [styles.mobile]: messagingView.isMobile,
                })}
            >
                {message}
            </div>
        )
    }
    if (isFetching && searchFocused) {
        return (
            <div
                ref={visibilityRef as React.RefObject<HTMLDivElement>}
                className={classNames(styles.loadingMessage, {
                    [styles.mobile]: messagingView.isMobile,
                })}
            >
                <div
                    className={classNames(
                        styles.loadingIndicator,
                        styles.filterLoading,
                    )}
                >
                    <Spinner size="xspx" />
                </div>
                <div>{t`Loading...`}</div>
            </div>
        )
    }
    if (isVisible && hasNoMessages && !isFetching) {
        if (filteredName) {
            return (
                <div
                    className={styles.noConversations}
                    data-testid="no-conversations"
                >
                    {t`No results found`}
                </div>
            )
        }
        return (
            <div
                ref={visibilityRef as React.RefObject<HTMLDivElement>}
                className={styles.threadListMessage}
            >
                <div className={styles.noMessagesContainer}>
                    <Envelope
                        height={ENVELOPE_ICON_SIZE}
                        width={ENVELOPE_ICON_SIZE}
                    />
                    <Typography
                        size={Sizes.mdpx}
                        weight={Weights.medium}
                        className={classNames(styles.title, {
                            [styles.desktop]: isDesktop,
                        })}
                    >{t`No Messages`}</Typography>
                    <Typography
                        size={Sizes.smpx}
                        className={classNames(styles.message, {
                            [styles.desktop]: isDesktop,
                        })}
                    >
                        {t(CAUTION_PASSWORD)}
                    </Typography>
                </div>
                <Button
                    text={t`Send New Message`}
                    color={ButtonColor.Blue}
                    size={ButtonSize.Medium}
                    className={classNames(styles.button, {
                        [styles.desktop]: isDesktop,
                    })}
                    onClick={handleUserClick}
                />
            </div>
        )
    }
    return (
        <div
            ref={visibilityRef as React.RefObject<HTMLDivElement>}
            className={styles.threadListContent}
        >
            <div
                ref={scrollRef}
                className={styles.threadListScrollable}
                data-testid="threadListScrollable"
                onScroll={() => {
                    if (!scrollRef.current) return
                    const scrollButtonThreshold =
                        scrollRef.current.clientHeight / 4
                    setShowScrollUpButton(
                        scrollRef.current.scrollTop > scrollButtonThreshold,
                    )
                }}
            >
                {!filteredName &&
                    draftThreads.map((thread: Thread) => (
                        <MessageThread
                            key={`${thread.from_user}-${thread.created_at}`}
                            thread={thread}
                            conversationListRef={scrollRef}
                        />
                    ))}
                {effectiveData?.threads?.map((thread: Thread) => (
                    <MessageThread
                        key={`${thread.from_user}-${thread.created_at}`}
                        thread={thread}
                        conversationListRef={scrollRef}
                        isFromSearch={!!filteredName}
                    />
                ))}
                {isFetching && (
                    <div className={styles.loadingIndicator}>
                        <Spinner size="xspx" />
                    </div>
                )}
            </div>
            {showScrollUpButton && (
                <ScrollButton
                    text={t`Top`}
                    className={styles.scrollButton}
                    scrollToDirection={ScrollButtonDirection.top}
                    onClick={() => {
                        if (!scrollRef.current) return
                        scrollRef.current.scrollTo({
                            top: 0,
                            behavior: "smooth",
                        })
                        addPageAction("DmScrollToTop")
                    }}
                />
            )}
        </div>
    )
}
